/**
 * Static skeleton shown during React Suspense fallback (lazy routes).
 * Mimics the Index page structure to minimize CLS and the "pop" effect
 * when the real content takes over.
 *
 * Strict rules (do not break):
 * - No hooks, no state, no effects, no fetches
 * - Only semantic tokens (bg-background, bg-muted, border-border, ...)
 * - Only Tailwind animate-pulse (CSS, GPU)
 * - No images, no custom fonts, no heavy libs
 */
const CardSkeleton = () => (
  <div className="bg-card rounded-lg border-l-4 border-l-muted p-3 space-y-2.5 h-[210px]">
    <div className="flex gap-3">
      <div className="shrink-0 w-16 h-16 rounded-md bg-muted" />
      <div className="flex-1 space-y-2">
        <div className="flex gap-2">
          <div className="h-5 w-20 bg-muted rounded" />
          <div className="h-5 w-16 bg-muted rounded" />
        </div>
        <div className="space-y-1">
          <div className="h-4 w-3/4 bg-muted rounded" />
          <div className="h-3 w-1/2 bg-muted rounded" />
        </div>
      </div>
    </div>
    <div className="flex gap-1">
      <div className="h-5 w-16 bg-muted rounded-full" />
      <div className="h-5 w-20 bg-muted rounded-full" />
    </div>
    <div className="flex items-center justify-between">
      <div className="h-6 w-32 bg-muted rounded" />
      <div className="h-4 w-12 bg-muted rounded" />
    </div>
  </div>
);

export const AppSkeleton = () => {
  return (
    <div className="min-h-screen bg-background animate-pulse">
      {/* Header placeholder — same height as the real header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b h-[var(--header-height)]">
        <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-muted" />
            <div className="h-4 w-28 bg-muted rounded" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-muted" />
            <div className="h-8 w-8 rounded-full bg-muted" />
            <div className="h-8 w-8 rounded-full bg-muted" />
          </div>
        </div>
      </div>

      {/* Compact filter bar placeholder */}
      <div className="border-b">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 overflow-hidden">
          <div className="h-8 w-20 bg-muted rounded-full shrink-0" />
          <div className="h-8 w-24 bg-muted rounded-full shrink-0" />
          <div className="h-8 w-16 bg-muted rounded-full shrink-0" />
          <div className="h-8 w-20 bg-muted rounded-full shrink-0" />
          <div className="h-8 w-24 bg-muted rounded-full shrink-0 hidden sm:block" />
        </div>
      </div>

      {/* Search bar placeholder */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="h-10 w-full bg-muted rounded-lg" />
      </div>

      {/* Cards grid placeholder */}
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="h-5 w-40 bg-muted rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </main>
    </div>
  );
};
